import { Help, Search } from '@mui/icons-material';
import { CircularProgress, TextField, InputAdornment } from '@mui/material';
import { SxProps } from '@mui/system';
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  useState,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import { BootstrapTooltip } from '@/components/atoms/tooltip/bootstrap';
import { ShowPassword } from '../password';

type StyledInputProps = {
  id?: string;
  name: string;
  label?: string | ReactNode;
  onFocus?: () => void;
  type:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  size?: 'medium' | 'small';
  margin?: 'normal' | 'dense' | 'none';
  required?: boolean;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
  helperText?: any;
  error?: boolean;
  sx?: SxProps;
  disabled?: boolean;
  defaultValue?: string | number | boolean;
  value?: string | number | boolean;
  shrink?: boolean;
  fullWidth?: boolean;
  tooltipMessage?: string;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  searchInput?: boolean;
  placeholder?: string;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  variant?: 'standard' | 'outlined' | 'filled';
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  height?: string;
};

export const StyledInput = ({
  id,
  label,
  margin,
  name,
  size,
  type,
  required = false,
  onFocus,
  onChange,
  onBlur,
  helperText,
  error,
  sx,
  disabled = false,
  defaultValue,
  value,
  shrink = undefined,
  fullWidth = false,
  tooltipMessage,
  multiline = false,
  rows,
  maxRows,
  searchInput,
  placeholder,
  loading,
  startIcon,
  endIcon,
  onKeyDown,
  variant = 'outlined',
  height = '44px',
  onClick,
}: StyledInputProps) => {
  const [typeState, setTypeState] = useState<string>(type);

  const generateStartAdornment = () => {
    if (startIcon) {
      return <InputAdornment position="start">{startIcon}</InputAdornment>;
    }
    return null;
  };

  const generateEndAdornment = () => {
    if (endIcon) {
      return <InputAdornment position="end">{endIcon}</InputAdornment>;
    }

    if (tooltipMessage) {
      return (
        <BootstrapTooltip title={tooltipMessage}>
          <Help cursor="pointer" color="action" />
        </BootstrapTooltip>
      );
    }

    if (type === 'password') {
      return <ShowPassword setTypeState={setTypeState} typeState={typeState} />;
    }

    if (loading) {
      return <CircularProgress size={20} color="primary" />;
    }

    if (searchInput) {
      return <Search sx={{ marginRight: (theme) => theme.spacing(1) }} />;
    }

    return null;
  };

  return (
    <TextField
      id={id}
      type={typeState}
      name={name}
      label={label}
      placeholder={placeholder}
      size={size}
      required={required}
      margin={margin}
      onFocus={onFocus}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      error={error ?? Boolean(helperText)}
      helperText={helperText}
      defaultValue={defaultValue}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          height,
        },
        ...sx,
      }}
      fullWidth={fullWidth}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      variant={variant}
      InputLabelProps={{ shrink }}
      InputProps={{
        startAdornment: generateStartAdornment(),
        endAdornment: generateEndAdornment(),
      }}
      inputProps={{ 'data-testid': name }}
      onKeyDown={onKeyDown}
      onClick={onClick}
    />
  );
};
